import { hasApiErrorCode, isPriceChanged, isSlotConflict } from '../lib/apiErrors'
import type { PaymentReturn } from '../lib/deeplink'
import type { PaymentPlan } from '../lib/payment'
import {
  canReuseHeldBooking,
  type HeldBooking,
  type PendingBookingSnapshot,
} from '../lib/pendingBooking'
import type {
  ConfirmWidgetCheckoutSessionResponse,
  CreateWidgetBookingRequest,
  CreateWidgetCheckoutSessionResponse,
  UUID,
  WidgetBookingPayment,
  WidgetBookingStatus,
} from '../types/PublicBookingChannelTypes'

/**
 * Taking a booking, and picking it back up after Stripe.
 *
 * Kept out of BookingWidget so the rules about when a booking is reused, when
 * a new one is made and what a payment return means can be tested without
 * rendering anything.
 */

/** What creating a booking told us, whichever endpoint made it. */
export interface CreatedBooking {
  bookingId: UUID
  /** Server-issued reference. Null when the endpoint does not send one. */
  reference: string | null
  /** Server status. Null when the endpoint does not send one; the channel plan decides. */
  status: WidgetBookingStatus | null
  payment: WidgetBookingPayment | null
}

export interface BookingSubmissionApi {
  createBooking: (body: CreateWidgetBookingRequest) => Promise<CreatedBooking>
  createCheckoutSession: (
    bookingId: UUID,
    returnUrl: string | null
  ) => Promise<CreateWidgetCheckoutSessionResponse>
  confirmPayment: (bookingId: UUID, sessionId: string) => Promise<ConfirmWidgetCheckoutSessionResponse>
}

export interface BookingSubmissionInput {
  body: CreateWidgetBookingRequest
  /** From bookingFingerprint, for the request in `body`. */
  fingerprint: string
  /** The channel plan's answer. The server's status wins whenever it gives one. */
  requiresPayment: boolean
  /** A booking already waiting on payment from an earlier attempt. */
  held: HeldBooking | null
  returnUrl: string | null
  /** Called once per booking actually created. */
  onCreated?: (created: CreatedBooking) => void
  /**
   * Called whenever the held booking changes: as soon as it exists, and again
   * once a Checkout session is open for it. Saving it here is what lets a retry
   * reuse it even when opening Checkout failed.
   */
  onHold: (held: HeldBooking) => void
}

export type BookingSubmissionOutcome =
  | {
      kind: 'booked'
      bookingId: UUID
      reference: string | null
      status: WidgetBookingStatus | null
      payment: WidgetBookingPayment | null
      /** True when payment was taken before this point. */
      paid: boolean
    }
  | {
      kind: 'checkout'
      checkoutUrl: string
      held: HeldBooking
      /** Checkout ends on the hosted booking page, not back here (ACA-111). */
      endsOnHostedPage: boolean
    }
  /** The time went between choosing it and booking it. Nothing was created. */
  | { kind: 'slotUnavailable' }
  /** A price is no longer the one shown (ACA-96). Nothing was created. */
  | { kind: 'priceChanged' }
  /** Paid with Klarna and Stripe has not confirmed it yet (ACA-105). The time stays held. */
  | { kind: 'processing'; bookingId: UUID; sessionId: string }

export type PaymentConfirmation =
  | {
      kind: 'paid'
      /** Null only from an older backend that does not report booking status. */
      status: 'CONFIRMED' | 'REQUESTED' | null
      reference: string | null
    }
  | { kind: 'unpaid' }
  /**
   * Checkout is finished but the money is not confirmed yet: a Klarna payment
   * Stripe is still processing (ACA-105). The time stays held until it settles.
   */
  | { kind: 'processing' }
  /**
   * The time was released. `paid` when the money arrived anyway (after the
   * hold ran out); `refunded` once the server has given it back.
   */
  | { kind: 'expired'; paid: boolean; refunded: boolean }

/** Reads confirm-payment's answer. */
export function interpretPaymentConfirmation(
  response: ConfirmWidgetCheckoutSessionResponse
): PaymentConfirmation {
  const reference = response.reference?.trim() || null
  const paid = response.paymentStatus === 'paid' || response.paymentStatus === 'no_payment_required'

  // `status` is Stripe's session status; the booking's own is `bookingStatus`.
  switch (response.bookingStatus) {
    case 'CONFIRMED':
    case 'REQUESTED':
      return { kind: 'paid', status: response.bookingStatus, reference }
    case 'EXPIRED':
      return { kind: 'expired', paid: response.paymentStatus === 'paid', refunded: response.refunded === true }
    case 'AWAITING_PAYMENT':
      // Paid at Stripe but not processed yet. Claim no more than a request:
      // telling someone they are booked in when the garage has not accepted is
      // the one mistake that sends them to the wrong place on the day.
      if (paid) return { kind: 'paid', status: 'REQUESTED', reference }
      // A finished Checkout whose money is still on its way (Klarna).
      return response.status === 'complete' ? { kind: 'processing' } : { kind: 'unpaid' }
    default:
      // An older backend sends no bookingStatus, so Stripe's word is all we have.
      return paid ? { kind: 'paid', status: null, reference } : { kind: 'unpaid' }
  }
}

/**
 * What to tell a customer whose time was released before their payment was
 * done. Someone whose money arrived late must hear that it is coming back,
 * not just "pick again".
 */
export function expiredHoldMessage(outcome: { paid: boolean; refunded: boolean }): string {
  if (outcome.refunded) {
    return "Your time was released and you've been refunded in full. Please pick another time."
  }
  if (outcome.paid) {
    return 'Your time was released before your payment arrived. It will be refunded in full. Please pick another time.'
  }
  return 'That time was released before payment finished. Pick a time again.'
}

/**
 * The reference to show. The server issues one; when it has none to send, the
 * same rule the server uses (`AC-` and the first six characters of the id) keeps
 * what the customer sees matching what the garage can search for.
 */
export function bookingReference(serverReference: string | null | undefined, bookingId: UUID): string {
  const trimmed = serverReference?.trim()
  if (trimmed) return trimmed
  return `AC-${bookingId.slice(0, 6).toUpperCase()}`
}

/**
 * The plan the confirmation shows, from what the server actually charged.
 * `undefined` means the server did not say, so the channel plan stands.
 */
export function planAfterBooking(
  plan: PaymentPlan,
  payment: WidgetBookingPayment | null | undefined
): PaymentPlan {
  if (payment === undefined) return plan
  if (payment === null) return { ...plan, mode: 'none', amountDue: null }

  const amount = Number(payment.amount)
  return {
    ...plan,
    mode: payment.mode,
    amountDue: Number.isFinite(amount) ? amount : plan.amountDue,
  }
}

async function openCheckout(
  api: BookingSubmissionApi,
  held: HeldBooking,
  input: BookingSubmissionInput
): Promise<BookingSubmissionOutcome> {
  const session = await api.createCheckoutSession(held.bookingId, input.returnUrl)
  if (!session.checkoutUrl) throw new Error('Missing checkout URL')

  const opened: HeldBooking = { ...held, sessionId: session.sessionId || held.sessionId }
  input.onHold(opened)
  return {
    kind: 'checkout',
    checkoutUrl: session.checkoutUrl,
    held: opened,
    endsOnHostedPage: session.endsOnHostedPage === true,
  }
}

/**
 * Retries payment on the booking already held. Returns null when that booking
 * can no longer be paid for and a new one is needed.
 */
async function retryHeld(
  api: BookingSubmissionApi,
  held: HeldBooking,
  input: BookingSubmissionInput
): Promise<BookingSubmissionOutcome | null> {
  try {
    return await openCheckout(api, held, input)
  } catch (error) {
    if (!hasApiErrorCode(error, 'BOOKING_NOT_AWAITING_PAYMENT')) throw error
  }

  // Not awaiting payment: paid in another tab, or the hold ran out. Only the
  // first of those may stop us making a new booking.
  if (held.sessionId) {
    const confirmation = interpretPaymentConfirmation(
      await api.confirmPayment(held.bookingId, held.sessionId)
    )
    if (confirmation.kind === 'paid') {
      return {
        kind: 'booked',
        bookingId: held.bookingId,
        reference: confirmation.reference ?? held.reference,
        status: confirmation.status,
        payment: held.payment ?? null,
        paid: true,
      }
    }
    // Paid with Klarna and still settling: wait for it, never book twice.
    if (confirmation.kind === 'processing') {
      return { kind: 'processing', bookingId: held.bookingId, sessionId: held.sessionId }
    }
  }

  return null
}

export async function submitBooking(
  api: BookingSubmissionApi,
  input: BookingSubmissionInput
): Promise<BookingSubmissionOutcome> {
  if (canReuseHeldBooking(input.held, input.fingerprint)) {
    const retried = await retryHeld(api, input.held, input)
    if (retried) return retried
  }

  let created: CreatedBooking
  try {
    created = await api.createBooking(input.body)
  } catch (error) {
    if (isPriceChanged(error)) return { kind: 'priceChanged' }
    if (isSlotConflict(error)) return { kind: 'slotUnavailable' }
    throw error
  }
  input.onCreated?.(created)

  const needsCheckout = created.status
    ? created.status === 'AWAITING_PAYMENT'
    : input.requiresPayment

  if (!needsCheckout) {
    return {
      kind: 'booked',
      bookingId: created.bookingId,
      reference: created.reference,
      status: created.status,
      payment: created.payment,
      paid: false,
    }
  }

  const held: HeldBooking = {
    bookingId: created.bookingId,
    fingerprint: input.fingerprint,
    sessionId: null,
    reference: created.reference,
    payment: created.payment,
  }
  input.onHold(held)

  return openCheckout(api, held, input)
}

export type PaymentReturnOutcome =
  | { kind: 'none' }
  | { kind: 'cancelled'; bookingId: UUID | null; snapshot: PendingBookingSnapshot | null }
  | {
      kind: 'paid'
      bookingId: UUID
      status: 'CONFIRMED' | 'REQUESTED' | null
      reference: string | null
      snapshot: PendingBookingSnapshot | null
    }
  | { kind: 'unpaid'; bookingId: UUID; snapshot: PendingBookingSnapshot | null }
  /** Paid with Klarna and not confirmed yet (ACA-105): wait on this session. */
  | {
      kind: 'processing'
      bookingId: UUID
      sessionId: string
      snapshot: PendingBookingSnapshot | null
    }
  /** The hold ran out before the payment completed, so the time is no longer kept. */
  | {
      kind: 'expired'
      bookingId: UUID
      paid: boolean
      refunded: boolean
      snapshot: PendingBookingSnapshot | null
    }
  | { kind: 'failed'; bookingId: UUID; error: unknown; snapshot: PendingBookingSnapshot | null }

/**
 * Works out what a page load that came back from Stripe means.
 *
 * The snapshot is only used when it belongs to the booking Stripe named, so a
 * leftover from another attempt can never dress up this one.
 */
export async function resolvePaymentReturn(args: {
  paymentReturn: PaymentReturn
  snapshot: PendingBookingSnapshot | null
  confirmPayment: BookingSubmissionApi['confirmPayment']
}): Promise<PaymentReturnOutcome> {
  const { paymentReturn, snapshot } = args
  if (!paymentReturn.isReturn) return { kind: 'none' }

  const bookingId = paymentReturn.bookingId ?? snapshot?.held.bookingId ?? null
  const matching =
    snapshot && bookingId && snapshot.held.bookingId === bookingId ? snapshot : null

  if (paymentReturn.cancelled) return { kind: 'cancelled', bookingId, snapshot: matching }

  const sessionId = paymentReturn.sessionId
  if (!bookingId || !sessionId) return { kind: 'none' }

  const current = matching ? { ...matching, held: { ...matching.held, sessionId } } : null

  try {
    const confirmation = interpretPaymentConfirmation(await args.confirmPayment(bookingId, sessionId))

    if (confirmation.kind === 'paid') {
      return {
        kind: 'paid',
        bookingId,
        status: confirmation.status,
        reference: confirmation.reference ?? current?.held.reference ?? null,
        snapshot: current,
      }
    }

    if (confirmation.kind === 'expired') {
      return {
        kind: 'expired',
        bookingId,
        paid: confirmation.paid,
        refunded: confirmation.refunded,
        snapshot: current,
      }
    }
    if (confirmation.kind === 'processing') {
      return { kind: 'processing', bookingId, sessionId, snapshot: current }
    }
    return { kind: 'unpaid', bookingId, snapshot: current }
  } catch (error) {
    return { kind: 'failed', bookingId, error, snapshot: current }
  }
}
